// Where "Capital & Context" can actually be listened to.
//
// Single source of truth for the public /podcast page's "Listen on" links. Hardcoded rather than
// read from the `podcast_channel` table because that table's row is empty — every column except
// `language` is NULL as of 2026-08-04, so it cannot drive anything yet. If/when the channel row is
// filled in via /admin/podcast, this file is the one place to switch over.
//
// `listed` is a DELIBERATE gate, not a feature flag: only platforms verified to actually carry the
// show are rendered. Linking a listener to a platform that does not host the podcast is worse than
// omitting it, so anything unverified stays `false` until someone confirms a real show URL.
//
// Verification performed 2026-08-04:
//   YouTube  LIVE     — playlist feed returns "The Capital and Context Podcast", current to Ep 21.
//   Spotify  LIVE     — show 0SClIaFLzaOt2844wjsKY2 resolves to "Capital & Context", Cape Berkshire
//                       Asset Management. NOTE: only carries Eps 1-14; it mirrors the anchor.fm RSS,
//                       which has not published since 20 May 2026.
//   Apple    NOT LISTED — absent from the iTunes Search index under both "capital and context cape
//                       berkshire" and "Capital & Context". The show has never been submitted.
//   Amazon   UNVERIFIED — the only URL we hold is a SEARCH QUERY, not a show page, and the Amazon
//                       Music web player is JS-gated so it could not be confirmed. Flip to `true`
//                       with a real /podcasts/<id> URL once someone checks in the Amazon app.

export type PodcastPlatform = {
  key: "youtube" | "spotify" | "apple" | "amazon" | "rss";
  label: string;
  url: string;
  /** Only `true` platforms are rendered to the public. See the verification note above. */
  listed: boolean;
  /** Brand colour for the platform chip. */
  className: string;
};

export const PODCAST_PLATFORMS: PodcastPlatform[] = [
  {
    key: "youtube",
    label: "YouTube",
    url: "https://www.youtube.com/playlist?list=PLM_Zuc6Rp11wKPTkPnECJmOq-l2uUUNH7",
    listed: true,
    className: "bg-red-600 hover:bg-red-700",
  },
  {
    key: "spotify",
    label: "Spotify",
    url: "https://open.spotify.com/show/0SClIaFLzaOt2844wjsKY2",
    listed: true,
    className: "bg-[#1DB954] hover:bg-[#1aa34a]",
  },
  {
    key: "apple",
    label: "Apple Podcasts",
    // No Apple Podcasts listing exists yet — see the verification note above.
    url: "",
    listed: false,
    className: "bg-[#9933CC] hover:bg-[#8a2eb8]",
  },
  {
    key: "amazon",
    label: "Amazon Music",
    // Search URL, not a show page. Not a verified listing.
    url: "https://music.amazon.com/podcasts/search/cape+berkshire",
    listed: false,
    className: "bg-sky-600 hover:bg-sky-700",
  },
];

/** The platforms the public page actually renders. */
export const LISTED_PLATFORMS = PODCAST_PLATFORMS.filter((p) => p.listed && p.url);

export const PODCAST_NAME = "Capital & Context";
export const PODCAST_TAGLINE =
  "Macro themes, asset allocation and the market stories that actually move portfolios — from the Cape Berkshire investment team.";
export const PODCAST_YOUTUBE_CHANNEL =
  "https://www.youtube.com/channel/UCOIb5G5C3nbvQ3cImNKTURw";
